import React, { useState } from 'react';
import { useAppState } from '../../context/AppStateProvider';
import { Lightbulb, Plus, Trash2, Edit2, X, Check } from 'lucide-react';

const WorkspaceIdeas = () => {
  const { ideas, addIdea, updateIdea, deleteIdea } = useAppState();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ title: '', content: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.title.trim() && formData.content.trim()) {
      if (editingId) {
        await updateIdea(editingId, formData);
        setEditingId(null);
      } else {
        await addIdea(formData.title.trim(), formData.content.trim());
      }
      setFormData({ title: '', content: '' });
      setShowForm(false);
    }
  };

  const handleEdit = (idea) => {
    setEditingId(idea.id);
    setFormData({ title: idea.title, content: idea.content });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({ title: '', content: '' });
  };

  return (
    <div className="workspace-container" data-testid="workspace-ideas">
      <div className="workspace-header">
        <div className="workspace-header-left">
          <Lightbulb className="workspace-icon" size={24} />
          <div>
            <h2 className="workspace-title">Ideas</h2>
            <p className="workspace-subtitle">{ideas.length} ideas captured</p>
          </div>
        </div>
        {!showForm && (
          <button 
            className="add-folder-btn"
            data-testid="add-idea-btn"
            onClick={() => setShowForm(true)}
          >
            <Plus size={18} />
            New Idea
          </button>
        )}
      </div>

      {showForm && (
        <div className="idea-form-container" data-testid="idea-form">
          <form onSubmit={handleSubmit} className="idea-form">
            <div className="form-group">
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Idea title..."
                className="idea-title-input"
                data-testid="idea-title-input"
                autoFocus
                required
              />
            </div>
            <div className="form-group">
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Describe your idea..."
                className="idea-content-textarea"
                data-testid="idea-content-input"
                rows="6"
                required
              />
            </div>
            <div className="idea-form-actions">
              <button type="submit" className="save-btn" data-testid="save-idea-btn">
                <Check size={16} />
                {editingId ? 'Update' : 'Save'}
              </button>
              <button type="button" onClick={handleCancel} className="cancel-btn" data-testid="cancel-idea-btn">
                <X size={16} />
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="ideas-list">
        {ideas.length === 0 && !showForm ? (
          <div className="empty-state-large" data-testid="empty-state">
            <Lightbulb size={48} className="empty-icon" />
            <p>No ideas yet</p>
            <p className="empty-hint">Capture your brilliant ideas and never forget them</p>
          </div>
        ) : (
          <div className="ideas-grid">
            {ideas.map(idea => (
              <div key={idea.id} className="idea-card" data-testid={`idea-${idea.id}`}>
                <div className="idea-card-header">
                  <h3 className="idea-card-title">{idea.title}</h3>
                  <div className="idea-card-actions">
                    <button
                      className="idea-action-btn"
                      data-testid={`edit-idea-${idea.id}`}
                      onClick={() => handleEdit(idea)}
                      title="Edit"
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      className="idea-action-btn idea-action-delete"
                      data-testid={`delete-idea-${idea.id}`}
                      onClick={() => deleteIdea(idea.id)}
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                <p className="idea-card-content">{idea.content}</p>
                <div className="idea-card-footer">
                  <span className="idea-timestamp">
                    {new Date(idea.createdAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkspaceIdeas;
